'use client';

import { useCallback, useEffect, useSyncExternalStore } from 'react';

import { api, useMutation, useQuery } from '@/lib/convex';
import {
  ensureBrowserNotificationsEnabled,
  hasBrowserNotificationOptOut,
  isStandaloneIosWebApp,
  isWebPushSupported,
  resolveBrowserNotificationStatus,
  setBrowserNotificationOptOut,
  shouldReconcileBrowserNotifications,
  updateImaiPushServiceWorker,
  type BrowserNotificationPermissionStatus,
} from '@/lib/notifications/web-push-client';
import type { DesktopNotificationUnavailableReason } from '@/lib/notifications/desktop-notification-status';
import {
  useDesktopNotificationAvailability,
  useDesktopNotificationPermission,
} from './desktop-notification-permission';

type BrowserSubscriptionSnapshot = {
  status: BrowserNotificationPermissionStatus;
  supported: boolean;
  permission: NotificationPermission | null;
  subscribed: boolean;
};

type SharedBrowserNotificationState = {
  status: BrowserNotificationPermissionStatus;
  operationPending: boolean;
};

export type BrowserNotificationPermissionController = {
  /** Website web push, or native notifications inside the desktop app. */
  surface: 'browser' | 'desktop';
  status: BrowserNotificationPermissionStatus;
  unavailableReason: DesktopNotificationUnavailableReason | null;
  settingsAction: 'toggle' | 'open_settings' | 'none';
  /** Desktop only: opens the fixed OS notification settings page. */
  openSystemSettings?: () => Promise<boolean>;
  configured: boolean;
  configurationPending: boolean;
  operationPending: boolean;
  enable(
    reason: 'task_follow' | 'thread_all_updates' | 'settings'
  ): Promise<BrowserNotificationPermissionStatus>;
  disable(): Promise<void>;
  refresh(options?: {
    reconcile?: boolean;
  }): Promise<BrowserNotificationPermissionStatus>;
  reconcile(): Promise<BrowserNotificationPermissionStatus>;
};

let sharedState: SharedBrowserNotificationState = {
  status: 'not_enabled',
  operationPending: false,
};
let operationQueue: Promise<void> | null = null;
let operationCount = 0;
let operationGeneration = 0;
type SharedRefreshRequest = {
  key: string;
  refresh(): Promise<BrowserNotificationPermissionStatus>;
  promise: Promise<BrowserNotificationPermissionStatus>;
  resolve(status: BrowserNotificationPermissionStatus): void;
};

let refreshInFlight: SharedRefreshRequest | null = null;
let pendingRefresh: SharedRefreshRequest | null = null;
let lifecycleListenerCount = 0;
let lastInitialRefreshKey: string | null = null;
const stateListeners = new Set<() => void>();
const lifecycleRefreshHandlers = new Set<() => void>();

function publishSharedState(next: Partial<SharedBrowserNotificationState>) {
  const nextState = { ...sharedState, ...next };
  if (
    nextState.status === sharedState.status &&
    nextState.operationPending === sharedState.operationPending
  ) {
    return;
  }
  sharedState = nextState;
  stateListeners.forEach((listener) => listener());
}

function subscribeToSharedState(listener: () => void) {
  stateListeners.add(listener);
  return () => stateListeners.delete(listener);
}

function readSharedState() {
  return sharedState;
}

async function readBrowserSubscriptionSnapshot(): Promise<BrowserSubscriptionSnapshot> {
  if (!isWebPushSupported() || !isStandaloneIosWebApp()) {
    return {
      status: 'unsupported',
      supported: false,
      permission: null,
      subscribed: false,
    };
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration('/');
    const subscription = await registration?.pushManager.getSubscription();
    const permission = Notification.permission;
    return {
      status: resolveBrowserNotificationStatus({
        supported: true,
        permission,
        subscribed: Boolean(subscription),
      }),
      supported: true,
      permission,
      subscribed: Boolean(subscription),
    };
  } catch {
    return {
      status: 'not_enabled',
      supported: true,
      permission: Notification.permission,
      subscribed: false,
    };
  }
}

function runSerializedBrowserOperation(
  operation: () => Promise<BrowserNotificationPermissionStatus>
) {
  operationCount += 1;
  operationGeneration += 1;
  publishSharedState({ operationPending: true });

  const run = async () => {
    try {
      const status = await operation();
      publishSharedState({ status });
      return status;
    } catch {
      publishSharedState({ status: 'not_enabled' });
      return 'not_enabled' as const;
    }
  };
  const result = operationQueue ? operationQueue.then(run, run) : run();
  operationQueue = result.then(
    () => undefined,
    () => undefined
  );

  return result.finally(() => {
    operationCount -= 1;
    if (operationCount === 0) {
      operationQueue = null;
      publishSharedState({ operationPending: false });
    }
  });
}

function createSharedRefreshRequest(
  key: string,
  refresh: () => Promise<BrowserNotificationPermissionStatus>
): SharedRefreshRequest {
  let resolve!: (status: BrowserNotificationPermissionStatus) => void;
  const promise = new Promise<BrowserNotificationPermissionStatus>((done) => {
    resolve = done;
  });
  return { key, refresh, promise, resolve };
}

function startSharedRefresh(request: SharedRefreshRequest) {
  refreshInFlight = request;
  void request
    .refresh()
    .then(request.resolve, () => request.resolve('not_enabled'))
    .finally(() => {
      if (refreshInFlight !== request) return;
      refreshInFlight = null;
      const followUp = pendingRefresh;
      pendingRefresh = null;
      if (followUp) void startSharedRefresh(followUp);
    });
  return request.promise;
}

function requestSharedRefresh(
  key: string,
  refresh: () => Promise<BrowserNotificationPermissionStatus>
) {
  if (!refreshInFlight) {
    return startSharedRefresh(createSharedRefreshRequest(key, refresh));
  }
  if (refreshInFlight.key === key) return refreshInFlight.promise;

  if (!pendingRefresh) {
    pendingRefresh = createSharedRefreshRequest(key, refresh);
  } else {
    pendingRefresh.key = key;
    pendingRefresh.refresh = refresh;
  }
  return pendingRefresh.promise;
}

function refreshFromLifecycle() {
  [...lifecycleRefreshHandlers].at(-1)?.();
}

function addLifecycleRefreshHandler(handler: () => void) {
  lifecycleRefreshHandlers.add(handler);
  lifecycleListenerCount += 1;
  if (lifecycleListenerCount === 1) {
    window.addEventListener('focus', refreshFromLifecycle);
    document.addEventListener('visibilitychange', refreshWhenVisible);
  }
  return () => {
    lifecycleRefreshHandlers.delete(handler);
    lifecycleListenerCount -= 1;
    if (lifecycleListenerCount === 0) {
      window.removeEventListener('focus', refreshFromLifecycle);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      lastInitialRefreshKey = null;
    }
  };
}

function refreshWhenVisible() {
  if (document.visibilityState === 'visible') refreshFromLifecycle();
}

export function useBrowserNotificationPermission(
  requested = true
): BrowserNotificationPermissionController {
  // Inside the desktop app the native controller replaces web push, which
  // Electron cannot subscribe to. The website path below is unchanged.
  const availability = useDesktopNotificationAvailability();
  const desktop = useDesktopNotificationPermission(requested, availability);
  const enabled = requested && availability === 'browser';
  const configuration = useQuery(
    api.notifications.subscriptions.getWebPushConfiguration,
    enabled ? {} : 'skip'
  );
  const register = useMutation(
    api.notifications.subscriptions.registerWebPushSubscription
  );
  const revoke = useMutation(
    api.notifications.subscriptions.revokeWebPushSubscription
  );
  const prepare = useMutation(
    api.notifications.subscriptions.prepareWebPushConfiguration
  );
  const shared = useSyncExternalStore(
    subscribeToSharedState,
    readSharedState,
    readSharedState
  );
  const publicKey = configuration.data?.publicKey ?? null;
  const configurationPending = enabled && configuration.isPending;
  const configured =
    enabled && configuration.data?.enabled === true && Boolean(publicKey);
  // A deployment generates its push keys on first use. The configuration
  // query updates on its own once they exist.
  const needsKeys = enabled && !configurationPending && !configured;
  useEffect(() => {
    if (needsKeys) void prepare({}).catch(() => undefined);
  }, [needsKeys, prepare]);
  const refreshKey = !enabled
    ? 'disabled'
    : configurationPending
      ? 'pending'
      : configured
        ? `configured:${publicKey}`
        : 'unconfigured';

  const refresh = useCallback(
    ({ reconcile = false }: { reconcile?: boolean } = {}) =>
      requestSharedRefresh(
        `${refreshKey}:${reconcile ? 'reconcile' : 'read'}`,
        async () => {
          const refreshGeneration = operationGeneration;
          const snapshot = await readBrowserSubscriptionSnapshot();
          if (refreshGeneration === operationGeneration) {
            publishSharedState({ status: snapshot.status });
          }

          if (
            !reconcile ||
            !snapshot.permission ||
            !shouldReconcileBrowserNotifications({
              configured,
              supported: snapshot.supported,
              permission: snapshot.permission,
              subscribed: snapshot.subscribed,
              optedOut: hasBrowserNotificationOptOut(),
            })
          ) {
            return snapshot.status;
          }

          return runSerializedBrowserOperation(async () => {
            const current = await readBrowserSubscriptionSnapshot();
            if (
              !current.permission ||
              !shouldReconcileBrowserNotifications({
                configured,
                supported: current.supported,
                permission: current.permission,
                subscribed: current.subscribed,
                optedOut: hasBrowserNotificationOptOut(),
              })
            ) {
              return current.status;
            }
            return await ensureBrowserNotificationsEnabled({
              publicKey,
              reason: 'settings',
              registerSubscription: async (subscription, deviceLabel) => {
                await register({ ...subscription, deviceLabel });
              },
            });
          });
        }
      ),
    [configured, publicKey, refreshKey, register]
  );

  const enable = useCallback(
    async (reason: 'task_follow' | 'thread_all_updates' | 'settings') => {
      if (configurationPending || !configured) return 'unsupported' as const;
      setBrowserNotificationOptOut(false);
      return runSerializedBrowserOperation(async () => {
        return await ensureBrowserNotificationsEnabled({
          publicKey,
          reason,
          registerSubscription: async (subscription, deviceLabel) => {
            await register({ ...subscription, deviceLabel });
          },
        });
      });
    },
    [configurationPending, configured, publicKey, register]
  );

  const disable = useCallback(async () => {
    setBrowserNotificationOptOut(true);
    await runSerializedBrowserOperation(async () => {
      if (isWebPushSupported()) {
        try {
          const registration =
            await navigator.serviceWorker.getRegistration('/');
          const subscription =
            await registration?.pushManager.getSubscription();
          if (subscription) {
            try {
              await revoke({ endpoint: subscription.endpoint });
            } finally {
              await subscription.unsubscribe().catch(() => false);
            }
          }
        } catch {
          // The server and browser both expire stale subscriptions independently.
        }
      }
      return (await readBrowserSubscriptionSnapshot()).status;
    });
  }, [revoke]);

  useEffect(() => {
    if (!enabled) return;

    const refreshAfterBrowserReturn = () => {
      void refresh({ reconcile: true });
    };
    const removeHandler = addLifecycleRefreshHandler(refreshAfterBrowserReturn);
    void updateImaiPushServiceWorker();
    if (lastInitialRefreshKey !== refreshKey) {
      lastInitialRefreshKey = refreshKey;
      refreshAfterBrowserReturn();
    }
    return removeHandler;
  }, [enabled, refresh, refreshKey]);

  if (desktop) return desktop;
  const status =
    enabled &&
    !configurationPending &&
    !configured &&
    shared.status !== 'blocked'
      ? 'unsupported'
      : shared.status;
  return {
    surface: 'browser',
    status,
    unavailableReason: null,
    settingsAction:
      status === 'blocked' || status === 'unsupported' ? 'none' : 'toggle',
    configured,
    configurationPending,
    operationPending: shared.operationPending,
    enable,
    disable,
    refresh,
    reconcile: () => refresh({ reconcile: true }),
  };
}
